import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import * as Dialog from '@radix-ui/react-dialog';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon } from '@fe-learner-common';
import { DialogTitle } from '@radix-ui/react-dialog';

export interface ModalProps extends Dialog.DialogProps {
  className?: string;
  overlayClassName?: string;
  children: ReactNode;
  actions?: ReactNode;
  Trigger?: FC<Dialog.DialogTriggerProps>;
  TitleContent?: ReactNode;
  onClose?: () => void;
  closable?: boolean;
  container: HTMLElement;
}

export const Modal: FC<ModalProps> = ({
  className,
  overlayClassName,
  children,
  actions,
  onClose,
  closable = true,
  TitleContent,
  Trigger,
  container,
  ...rest
}) => {
  const intl = useIntl();

  return (
    <Dialog.Root {...rest}>
      {Trigger && <Trigger />}
      <Dialog.Portal container={container}>
        <Dialog.Overlay
          className={cn(
            'data-[state=open]:animate-overlayShow fixed inset-0 bg-black opacity-[10%]',
            overlayClassName,
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={cn(
            'data-[state=open]:animate-contentShow fixed left-6 right-6 top-[50%] flex max-h-[90vh] translate-y-[-50%] flex-col rounded-2xl bg-neutral-0  shadow-100',
            'md:left-[50%] md:right-auto md:translate-x-[-50%]',
            className,
          )}
        >
          {TitleContent || closable ? (
            <div
              className={cn('flex items-center gap-x-3 p-3 ps-6', {
                'border-b-1 border-neutral-100': !!TitleContent,
              })}
            >
              <DialogTitle asChild>{TitleContent}</DialogTitle>

              {closable ? (
                <Dialog.Close asChild>
                  <button
                    className="l-btn l-btn-tertiary l-btn-md l-icon-btn ms-auto"
                    aria-label={intl.formatMessage({
                      id: 'learner.common.close',
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onClose) {
                        onClose();
                      }
                    }}
                  >
                    <FaIcon icon={faXmark} width={20} height={20} />
                  </button>
                </Dialog.Close>
              ) : null}
            </div>
          ) : null}

          <div className="h-full overflow-auto">{children}</div>
          {actions ? (
            <div className="flex flex-wrap justify-end gap-2 border-t-2 border-neutral-100 px-6 py-4 ">
              {actions}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const StatusModal: FC<ModalProps> = ({
  className,
  children,
  ...rest
}) => (
  <Modal className={cn(className, 'sm:w-[720px]')} {...rest}>
    {children}
  </Modal>
);
